import { useEffect } from "react";

import Head from "next/head";
import { useRouter } from "next/router";

import { dehydrate, useQuery } from "@tanstack/react-query";
import { LoadingSpinner } from "@newsela/angelou";

import BaseLayout from "@/base";
import RootProviders from "@/base/RootProviders";
import ErrorMessage from "@/errors/ErrorMessage";
import NotLoggedInError from "@/errors/NotLoggedInError";
import { useGetUser } from "@/users/v3";
import useUserFeaturesQuery from "@/users/v3/useUserFeaturesQuery";
import useGetUserExperimentSegment from "@/features/useGetUserExperimentSegment";
import getQueryClient from "@/utils/getQueryClient";
import { headlessTreeQuery } from "@/bundles/queries/headlessTreeQuery";

import BundlePage from "@/bundles/BundlePage";

import type { NextPageContext } from "next";

const BundlePageRoute = () => {
  const { push, query } = useRouter();
  const contentId = query["contentId"] as string;

  const { data: user, isLoading: isLoadingUser, isError } = useGetUser();
  const { data: features, isLoading: isUserFeaturesLoading } =
    useUserFeaturesQuery();
  const { data: treeData } = useQuery(headlessTreeQuery(contentId));
  const rootBundleName = treeData?.items[treeData?.root_item_id]?.name;

  const hasSTEMLicense = features?.includes("has_stem_license");

  const [newBundlesExperience] = useGetUserExperimentSegment(
    "bundles_page_rebuild",
  );
  const isBundleExperimentActive = newBundlesExperience === "on";

  const canSeeNewBundlesExperience = hasSTEMLicense || isBundleExperimentActive;

  useEffect(() => {
    if (features) {
      if (!canSeeNewBundlesExperience) {
        // Redirect to old subject page.
        push(`/content/${contentId}`);
      }
    }
  }, [features, canSeeNewBundlesExperience]);

  const renderContent = (mainContents: JSX.Element) => (
    <>
      <Head>
        <title>Newsela | {rootBundleName ?? "Content Collections"}</title>
      </Head>
      <BaseLayout mainContents={mainContents} />
    </>
  );

  if (isUserFeaturesLoading || isLoadingUser) {
    return renderContent(<LoadingSpinner />);
  }

  if (isError) {
    return renderContent(
      <ErrorMessage headline="Oh no! Something went wrong.">
        <p>Either you do not have access, or an error has occurred.</p>
      </ErrorMessage>,
    );
  }

  if (!user?.is_authenticated) {
    return renderContent(<NotLoggedInError />);
  }

  return renderContent(<BundlePage />);
};

export const getServerSideProps = async (ctx: NextPageContext) => {
  const contentId = ctx.query?.["contentId"] as string;
  const queryClient = getQueryClient();

  const [RootProvidersInitialProps] = await Promise.all([
    RootProviders.__getInitialProps({ ctx }),
    queryClient.prefetchQuery(headlessTreeQuery(contentId, ctx)),
  ]);

  return {
    props: {
      [RootProviders.InitialPropsName]: RootProvidersInitialProps,
      dehydratedState: dehydrate(queryClient),
    },
  };
};

export default BundlePageRoute;
